import type { User } from './user';

export enum GroupStatusEnum {
  Unknown = 0,
  Normal = 1,
  DISBAND = 2,
  BAN = 3,
  LIMIT = 4,
}

export enum GroupSearchEnum {
  Private = 0,
  Public = 1,
}

export enum GroupEncryptionEnum {
  NO = 0,
  IS = 1,
}

export enum GroupMemberOperationType {
  ADD = 1,
  UPDATE = 2,
  DELETE = 3,
}

export enum GroupMemberRole {
  NONE = 0,
  OWNER = 1,
  ADMIN = 2,
  MEMBER = 3,
  STRANGER_INVITE = 4,
}

export enum GroupJoinStatusEnum {
  Pending = 1,
  Joined = 2,
  Rejected = 3,
}

export interface GroupInfo {
  avatar: string;
  avatar_color: string;
  created_at: number;
  creator_uid: string;
  description: string;
  gid: string;
  group_id: string;
  group_name: string;
  group_status: GroupStatusEnum;
  is_encryption: GroupEncryptionEnum;
  join_type: number;
  related_gid: string;
  search_status: GroupSearchEnum;
  status: GroupStatusEnum;
  share_url: string;
  member_num: number;
  join_status: GroupJoinStatusEnum;
  risk_end_time: number;
  is_risk: number;
}

export enum GroupOptionBodyEnum {
  Everyone = 1,
  Admin = 2,
  Nobody = 3,
}

export enum GroupOptionDisableEnum {
  Enable = 1,
  Disable = 2,
}

export interface GroupOptionOriginal {
  key: string;
  val: number;
  category: number;
}

export enum GroupMemberJoinMode {
  Creator = 1,
  Invite = 2,
  Search = 3, // default
  Scan = 4,
  Link = 5,
}

export interface GroupMyRole {
  role: GroupMemberRole | undefined;
  join_mode?: GroupMemberJoinMode;
  invite_uid?: string;
}

export enum GroupOptionKey {
  MEMBER_JOIN_NOTICE = 'member_join_notice',
  MEMBER_LEAVE_NOTICE = 'member_leave_notice',
  INFO_EDIT = 'info_edit',
  AT_ALL = 'at_all',
  SEND_MESSAGE = 'send_message',
  MEDIA_CALL = 'media_call',
  PRIVATE_MESSAGE = 'private_message',
  GLOBAL_SEARCH = 'global_search',
  ENTER_AUDIT = 'enter_audit',
  HIDE_MEMBER = 'hide_member',
  RESTRICT_DOWNLOAD = 'restrict_download',
  RESTRICT_FORWARD = 'restrict_forward',
  RESTRICT_SCREENSHOT = 'restrict_screenshot',
  RESTRICT_COPING = 'restrict_coping',
  RESTRICT_MEMBER_RANGE = 'restrict_member_range',
  RESTRICT_SCREEN_RECORDING = 'restrict_screen_recording',
}

export interface GroupOptions {
  group_id: string;
  member_join_notice: {
    val: GroupOptionBodyEnum;
    category: number; // 1: body, 2: disable
  };
  member_leave_notice: {
    val: GroupOptionBodyEnum;
    category: number;
  };
  info_edit: {
    val: GroupOptionBodyEnum;
    category: number;
  };
  at_all: {
    val: GroupOptionBodyEnum;
    category: number;
  };
  send_message: {
    val: GroupOptionBodyEnum;
    category: number;
  };
  media_call: {
    val: GroupOptionBodyEnum;
    category: number;
  };
  private_message: {
    val: GroupOptionBodyEnum;
    category: number;
  };
  global_search: {
    val: GroupOptionDisableEnum;
    category: number;
  };
  enter_audit: {
    val: GroupOptionDisableEnum;
    category: number;
  };
  hide_member: {
    val: GroupOptionDisableEnum;
    category: number;
  };
  restrict_download: {
    val: GroupOptionDisableEnum;
    category: number;
  };
  restrict_forward: {
    val: GroupOptionDisableEnum;
    category: number;
  };
  restrict_screenshot: {
    val: GroupOptionDisableEnum;
    category: number;
  };
  restrict_screen_recording: {
    val: GroupOptionDisableEnum;
    category: number;
  };
  restrict_coping: {
    val: GroupOptionDisableEnum;
    category: number;
  };
  restrict_member_range: {
    val: GroupOptionBodyEnum;
    category: number;
  };
}

export interface GroupPosition {
  position: number;
  name: string;
}

export interface GroupMemberInfo {
  user_id: string;
  role: GroupMemberRole;
  status: GroupStatusEnum;
  update_at: number;
}

export enum DisallowSendCategory {
  Whitelist = 1,
  Blacklist = 2,
}

export interface GroupDisallowSendInfo {
  settings_key: string;
  settings_val: DisallowSendCategory;
  whitelist: DisallowSendUserInfo[];
  blacklist: DisallowSendUserInfo[];
}

export interface DisallowSendUserInfo {
  uid: string;
  expire_time: number;
}

export interface GroupMemberInfoWithOperationType extends GroupMemberInfo {
  operation_type: GroupMemberOperationType;
}

export type GroupMember = GroupMemberInfo & User;
